import {
  CheckSquare,
  FileSignature,
  FileStack,
  Gauge,
  Landmark,
  Layers,
  LifeBuoy,
  ReceiptText,
  Shield,
  StickyNote,
  Users,
} from "lucide-react";
import { lazy } from "react";

import { HubspotNavIcon } from "@/components/customers/CrmLinkBadge";
import type { TabRoute } from "@/lib/routes";

// Lazy-load each tab so visiting a customer only pulls the active tab's
// chunk. Tab components default-export their named component to satisfy
// React.lazy's shape.
const ContactsTab = lazy(() => import("./ContactsTab"));
const ClientProfileTab = lazy(() => import("./ClientProfileTab"));
const AccountsTab = lazy(() => import("./AccountsTab"));
const BillsTab = lazy(() => import("./BillsTab"));
const ContractsTab = lazy(() => import("./ContractsTab"));
const LoaLoeTab = lazy(() => import("./LoaLoeTab"));
const CcaDetailsTab = lazy(() => import("./CcaDetailsTab"));
const DealsTab = lazy(() => import("./DealsTab"));
const TasksTab = lazy(() => import("./TasksTab"));
const CasesTab = lazy(() => import("./CasesTab"));
const NotesTab = lazy(() => import("./NotesTab"));
const HubspotActivityTab = lazy(() => import("./HubspotActivityTab"));

/**
 * Child tab routes for `/customers/:id` — the one client detail page. A
 * client is a client: there is no "identity data" vs "activity data" split
 * across two pages any more (that used to be Global's `/apps/clients/:id` vs
 * Sales's own `/customers/:id`, bridged by manual cross-links; `/apps/*` no
 * longer resolves at all). All tabs sit flat in one bar, no visual grouping
 * between them.
 *
 * Exported from here (not inline in `lib/global-routes.tsx`) so the parent
 * page can import this list directly without going through `NAV_RECORDS`.
 * That avoids a circular import: `CustomerDetailPage` imports tab routes,
 * `NAV_RECORDS` imports `CustomerDetailPage` as the parent element.
 *
 * Both `NAV_RECORDS` (for router child-route materialization) and
 * `CustomerDetailPage` (for `<RouterTabs routes={...} />`) read from this
 * same source so the tab bar and the router stay in lock-step.
 */
export const CUSTOMER_TAB_ROUTES: ReadonlyArray<TabRoute> = [
  {
    path: "contacts",
    label: "Contacts",
    icon: Users,
    element: <ContactsTab />,
    requiredPermission: "customers.contacts.manage",
  },
  {
    // Part of the base client view — no dedicated permission (the parent
    // `/customers/:id` route already gates on `customers.view`).
    path: "client-profile",
    label: "Client Profile",
    icon: Shield,
    element: <ClientProfileTab />,
  },
  {
    // Merges the former "Bill Copies" and "Bill Analyzer" tabs into one
    // trigger: BillsTab renders an underline switcher between the two,
    // rendering EACH sub-tab's original component unchanged. No permission of
    // its own, so a user without `customers.portfolio_analyzer.manage` still
    // sees a normal single-purpose Bills tab — BillsTab itself hides the
    // Analyzer switcher option from them.
    path: "bills",
    label: "Bills",
    icon: ReceiptText,
    element: <BillsTab />,
  },
  {
    path: "accounts",
    label: "Accounts",
    icon: Gauge,
    element: <AccountsTab />,
    requiredPermission: "customers.accounts.manage",
  },
  {
    // Merges intake (upload, versions, extraction status — formerly the
    // standalone "Supply Contracts" tab) and the "Contract Analyzer"
    // create/manage sub-tab into one trigger: ContractsTab renders an
    // underline switcher between the two, same pattern as `BillsTab`.
    // Presentation (comparison grid, ranking, single-contract breakdown,
    // PDF) always lives in Energy Advisor's "Contract Analyzer" tool.
    path: "contracts",
    label: "Contracts",
    icon: FileStack,
    element: <ContractsTab />,
    requiredPermission: "customers.contract_analyzer.manage",
  },
  {
    path: "loa",
    label: "LOA/LOE",
    icon: FileSignature,
    element: <LoaLoeTab />,
    requiredPermission: "customers.loas.manage",
  },
  {
    // Part of the base client view — no dedicated permission. Rendered only
    // for customers whose business unit is CCA: `CustomerDetailPage`
    // filters it out of the tab bar for every other segment (AB#7087).
    path: "cca",
    label: "CCA Details",
    icon: Landmark,
    element: <CcaDetailsTab />,
  },
  {
    // Part of the base client view — no dedicated permission.
    path: "deals",
    label: "Deals",
    icon: Layers,
    element: <DealsTab />,
    // A deal opened from this tab is the global record page `/deals/:id`.
    // Keeps the Energy Advisor sidebar's Client › Deals row lit there —
    // `DealDetailPage` adopts the deal's client into the
    // advisor picker, so the lit row is that deal's client's. Nav-only; the
    // client page's own tab strip ignores it.
    alsoActiveOn: ["/deals/:dealId"],
  },
  {
    path: "tasks",
    label: "Tasks",
    icon: CheckSquare,
    element: <TasksTab />,
    requiredPermission: "tasks.view",
  },
  {
    // Next to Tasks, per the ticket. Ungated by the shell flag for the same
    // reason as the deal's Cases tab: the Cases page ships in both shells.
    path: "cases",
    label: "Cases",
    icon: LifeBuoy,
    element: <CasesTab />,
    requiredPermission: "cases.view",
  },
  {
    // Directly below Cases, per the ticket (AB#8816). Part of the base client
    // view — no permission of its own to read; Add New / Edit / Delete check
    // `customers.notes.manage` inside the tab.
    path: "notes",
    label: "Notes and Attachments",
    icon: StickyNote,
    element: <NotesTab />,
  },
  {
    // HubSpot activity as a page rather than this page's collapsible rail.
    // Filed here, with the client's own tabs, because that's where the data
    // originates — it's this record's HubSpot link, like Contacts or Bills.
    // The Energy Advisor sidebar picks it up by listing the record tabs, so
    // it needs no advisor-specific declaration. The advisor sidebar has no
    // room for a pushing rail, which is why this is a tab there.
    // The HubSpot mark rather than a generic icon, since it names an
    // external system.
    path: "hubspot",
    label: "HubSpot Activity",
    icon: HubspotNavIcon,
    element: <HubspotActivityTab />,
  },
];
